import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../api/client';
import { useApi } from '../api/useApi';

interface Priority {
  priorityCategoryId: string;
  l1: string;
  l2: string;
  l3: string;
  weight: 'H' | 'M' | 'L';
  evidence?: string;
  confidence?: number;
}

export default function CallReview() {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  // reloadKey lets us refetch the review in place after (re-)analyzing.
  const [reloadKey, setReloadKey] = useState(0);
  const { data: review, loading, error } = useApi<any>(
    () => API.getReview(`${callId}-call`),
    [callId, reloadKey],
  );

  const { data: settings } = useApi<any>(() => API.getSettings(), []);
  const fyLabel = settings?.currentYear ? `FY${String(settings.currentYear).slice(-2)}` : null;

  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [transcript, setTranscript] = useState('');
  const [activeTab, setActiveTab] = useState<'priorities' | 'transcript'>('priorities');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const [addId, setAddId] = useState('');

  // Seed editable state from the review once it loads. Before any analysis has
  // run, land the user on the (editable) Transcript tab so they can paste/edit
  // and analyze; afterwards default to the detected priorities.
  useEffect(() => {
    if (!review) return;
    setPriorities(review.priorities.map((p: Priority) => ({ ...p })));
    setTranscript(review.transcript ?? '');
    setActiveTab(review.analyzed ? 'priorities' : 'transcript');
  }, [review]);

  const W: Record<string, string> = { H: 'w-h', M: 'w-m', L: 'w-l' };

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !review) return <div className="phil-error">Unable to load call review. {error}</div>;

  const fi = { id: callId, name: review.fiName };
  const attendees = review.attendees ?? {};

  // Persist transcript edits when the editor loses focus.
  const persist = () => {
    API.saveTranscript(`${fi.id}-call`, transcript).catch(() => {});
  };

  const handleAnalyze = async () => {
    setAnalyzeError('');
    setAnalyzing(true);
    try {
      await API.analyzeCall(`${fi.id}-call`, transcript);
      setReloadKey(k => k + 1); // refetch review → reseeds priorities, flips to Priorities tab
    } catch (e: any) {
      setAnalyzeError(e?.message ?? 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const setWeight = (i: number, w: 'H' | 'M' | 'L') => {
    const next = [...priorities];
    next[i] = { ...next[i], weight: w };
    setPriorities(next);
  };

  const removePriority = (i: number) => setPriorities(priorities.filter((_, idx) => idx !== i));

  // Categories from the catalogue that aren't already in the list — the pool
  // for manually adding a priority.
  const available: Priority[] = (review.categories ?? []).filter(
    (c: Priority) => !priorities.some(p => p.priorityCategoryId === c.priorityCategoryId),
  );

  const addPriority = () => {
    const cat = available.find(c => c.priorityCategoryId === addId);
    if (!cat) return;
    setPriorities([...priorities, { ...cat, weight: 'M' }]); // default weight, tune via H/M/L
    setAddId('');
  };

  const handleSave = async () => {
    if (!priorities.length && !window.confirm(
      'This will clear all saved priorities for this FI for the current program year. Continue?'
    )) {
      return;
    }
    setSaveError('');
    setSaving(true);
    try {
      await API.saveReview(`${fi.id}-call`, {
        priorities: priorities.map(p => ({ priorityCategoryId: p.priorityCategoryId, weight: p.weight })),
      });
      setSaved(true);
      setTimeout(() => navigate(`/fi/${fi.id}/notes`), 800);
    } catch (e: any) {
      setSaveError(e?.message ?? 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="breadcrumb">
        <a onClick={() => navigate(`/fi/${fi.id}`)}>{fi.name}</a>
        <span className="bc-sep">›</span>
        <span>Post-Call Review</span>
      </div>

      <div className="page-header">

        <h1>Post-Call Review · {fi.name}</h1>
      </div>

      <div className="tabs">
        <div className={`tab${activeTab === 'transcript' ? ' active' : ''}`} onClick={() => setActiveTab('transcript')}>
          Transcript
        </div>
        <div className={`tab${activeTab === 'priorities' ? ' active' : ''}`} onClick={() => setActiveTab('priorities')}>
          Detected Priorities
        </div>
      </div>

      {activeTab === 'priorities' && (
        <div className="card">
          <div className="card-header">
            {fyLabel && <h2>{fyLabel} Priorities — extracted from transcript (editable)</h2>}
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Attendees: {attendees.execSponsor} (ES), {attendees.chaperone} (Chap), {attendees.filDirector} (FIL)
            </span>
          </div>
          <div className="card-body">
            {saveError && <div className="phil-error" style={{ marginBottom: 12 }}>{saveError}</div>}
            {!priorities.length && (
              <div style={{ fontSize: 13, color: '#6b7280', padding: '8px 0' }}>
                {review.analyzed
                  ? 'No priorities were detected in the transcript — add them manually below.'
                  : 'No analysis yet — run “Analyze Transcript”, or add priorities manually below.'}
              </div>
            )}
            {priorities.map((p, i) => (
              <div key={p.priorityCategoryId} className="priority-row-edit" style={{ alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className={W[p.weight]}>{p.weight}</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{p.l3}</span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{p.l1} › {p.l2}</span>
                  </div>
                  {p.evidence && (
                    <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic', paddingLeft: 2 }}>
                      “{p.evidence}”
                      {typeof p.confidence === 'number' && (
                        <span style={{ color: '#9ca3af', fontStyle: 'normal' }}> · {Math.round(p.confidence * 100)}% conf.</span>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {(['H', 'M', 'L'] as const).map(w => (
                    <button
                      key={w}
                      className={`btn btn-sm ${p.weight === w ? '' : 'btn-ghost'}`}
                      style={{
                        padding: '2px 8px', fontSize: 11,
                        ...(p.weight === w
                          ? w === 'H'
                            ? { background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }
                            : w === 'M'
                            ? { background: '#fef9c3', color: '#92400e', border: '1px solid #fde68a' }
                            : { background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }
                          : {}),
                      }}
                      onClick={() => setWeight(i, w)}
                    >
                      {w}
                    </button>
                  ))}
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ padding: '2px 8px', fontSize: 11, color: '#dc2626' }}
                    onClick={() => removePriority(i)}
                    title="Remove this priority"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: priorities.length ? 16 : 4, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              <select
                value={addId}
                onChange={e => setAddId(e.target.value)}
                disabled={!available.length}
                style={{ flex: 1, fontSize: 13, padding: '6px 8px' }}
              >
                <option value="">
                  {available.length ? 'Add a priority manually…' : 'All priorities added'}
                </option>
                {available.map(c => (
                  <option key={c.priorityCategoryId} value={c.priorityCategoryId}>
                    {c.l1} › {c.l2} › {c.l3}
                  </option>
                ))}
              </select>
              <button className="btn btn-outline btn-sm" onClick={addPriority} disabled={!addId}>
                + Add
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                className={`btn btn-sm ${saved ? 'btn-outline' : 'btn-success'}`}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : saved ? '✓ Saved' : '✓ Save Priorities'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'transcript' && (
        <div className="card">
          <div className="card-header">
            <h2>Transcript</h2>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Paste or edit the transcript, then analyze</span>
          </div>
          <div className="card-body">
            {analyzeError && <div className="phil-error" style={{ marginBottom: 12 }}>{analyzeError}</div>}
            <textarea
              className="transcript-input"
              value={transcript}
              onChange={e => setTranscript(e.target.value)}
              onBlur={persist}
              placeholder={'Paste the call transcript here.\n\nSpeaker 1: …\nSpeaker 2: …'}
              rows={20}
              style={{ width: '100%', fontSize: 13, lineHeight: 1.6, padding: 12, fontFamily: 'inherit', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                {transcript.trim() ? `${transcript.trim().split(/\s+/).length} words` : 'No transcript yet'}
              </div>
              <button
                className="btn btn-outline btn-sm"
                onClick={handleAnalyze}
                disabled={!transcript.trim() || analyzing}
              >
                {analyzing ? 'Analyzing…' : review.analyzed ? '↺ Re-analyze' : 'Analyze Transcript →'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate(`/fi/${fi.id}/notes`)}>Export Notes →</button>
        <button className="btn btn-outline btn-sm" onClick={() => navigate(`/calls/${fi.id}/thank-you`)}>Thank-You Email →</button>
      </div>
    </>
  );
}
